import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { Dropdown, Grid, Icon, Image, Label, StrictDropdownProps, TextArea } from 'semantic-ui-react'
import description from '../../../../assets/svg/act_detail_description.svg'
import member from '../../../../assets/svg/act_detail_member.svg'
import labels from '../../../../assets/svg/act_detail_noun_label.svg'
import phase from '../../../../assets/svg/act_detail_phase.svg'
import timelines from '../../../../assets/svg/act_detail_timelines.svg'
import total_hours from '../../../../assets/svg/act_detail_total_hours.svg'
import budget from '../../../../assets/svg/acts_detail_expenditures.svg'
import DatepickerComponent from '../../../../components/Datepicker/Datepicker'
import { IUserDetail } from '../../../../types/user'
import { checkRole, checkTeamMemberOrAdminStatus } from '../../../../utils/checkRole'
import { currencyFormat } from '../../../../utils/currencyFormat'
import { durationToHours } from '../../../../utils/durationToHours'
import { statusColor } from '../../../../utils/statusColor'
import { IEmployee } from '../../../settings/types/employee'
import { IPassThroughCost } from '../../types/directAndPassTroughCosts'
import { IProject, IProjectLabel, IProjectPhase } from '../../types/project'
import TeamGroupComponent from '../list/TeamGroupComponent'
import './ActivityDetails.css'

interface StateProps {
  cost: IPassThroughCost
  project: IProject
  employees: IEmployee[]
  costName: string
  user: IUserDetail | null
}

interface DispatchProps {
  updateCost: (projectId: string | number, cost: IPassThroughCost, closedDatepicker: boolean) => void
}

type Props = StateProps & DispatchProps

const PassTroughDetailsOverviewComponent = (props: Props): JSX.Element => {
  const { user, cost, project, employees, costName, updateCost } = props

  const [costState, setCostState] = useState({ ...cost, statusColor: (status: string) => statusColor(status) })
  const [openStartDatePicker, setOpenStartDatePicker] = useState(false)
  const [openEndDatePicker, setOpenEndDatePicker] = useState(false)
  const [editRight, setEditRight] = useState(false)
  const timer = useRef<number>(0)

  useEffect(() => {
    setCostState({ ...cost })
    if (user) {
      setEditRight(checkRole(user, [15]) && checkTeamMemberOrAdminStatus(user, project))
    }
  }, [cost, setCostState, user, project])

  useEffect(() => {
    setCostState(prev => { return { ...prev, offer_position: costName } })
  }, [costName, setCostState])

  // change dates

  const onInputClickStart = () => {
    if (openStartDatePicker) {
      setOpenStartDatePicker(false)
    } else {
      setOpenEndDatePicker(false)
      setOpenStartDatePicker(true)
    }
  }

  const onInputClickEnd = () => {
    if (openEndDatePicker) {
      setOpenEndDatePicker(false)
    } else {
      setOpenStartDatePicker(false)
      setOpenEndDatePicker(true)
    }
  }

  const onClickOutsideStart = () => {
    setOpenStartDatePicker(false)
    updateCost(project.id, { ...costState, start_date: cost.start_date, end_date: cost.end_date }, false)
  }

  const onClickOutsideEnd = () => {
    setOpenEndDatePicker(false)
    updateCost(project.id, { ...costState, start_date: cost.start_date, end_date: cost.end_date }, false)
  }

  const changeStartDate = (date: Date) => {
    if (moment(date).isSameOrBefore(moment(costState.end_date))) {
      const _start = date ? moment(date).format('YYYY-MM-DD') : null
      updateCost(project.id, { ...costState, start_date: _start }, false)
      setCostState({ ...costState, start_date: _start })
      setOpenStartDatePicker(false)
    }
  }

  const changeEndDate = (date: Date) => {
    if (moment(date).isSameOrAfter(moment(costState.start_date))) {
      const _end = date ? moment(date).format('YYYY-MM-DD') : null
      updateCost(project.id, { ...costState, end_date: _end }, false)
      setCostState({ ...costState, end_date: _end })
      setOpenEndDatePicker(false)
    }
  }

  const changeEmployees = (data: StrictDropdownProps) => {
    if (data && data.value) {
      const value = data.value.toString()
      const _employees: IEmployee[] = []
      value.split(',').forEach(d => {
        employees.forEach(e => {
          if (e.email === d && !_employees.includes(e)) {
            _employees.push(e)
          }
        })
      })
      updateCost(project.id, { ...costState, assigned_employees: _employees }, openEndDatePicker)
      setCostState(prev => { return { ...prev, assigned_employees: _employees } })
    }
  }

  const changeLabels = (data: StrictDropdownProps) => {
    if (data && data.value) {
      const value = data.value.toString()
      const _labels: IProjectLabel[] = []
      value.split(',').forEach(l => {
        project.labels.forEach(pl => {
          if (+pl.id === +l && !_labels.includes(pl)) {
            _labels.push(pl)
          }
        })
      })
      updateCost(project.id, { ...costState, labels: _labels }, openEndDatePicker)
      setCostState(prev => { return { ...prev, labels: _labels } })
    }
  }

  const changePhases = (data: StrictDropdownProps) => {
    if (data && data.value) {
      const value = data.value.toString()
      const _phases: IProjectPhase[] = []
      value.split(',').forEach(l => {
        project.phases.forEach(pl => {
          if (+pl.id === +l && !_phases.includes(pl)) {
            _phases.push(pl)
          }
        })
      })
      updateCost(project.id, { ...costState, project_phases: _phases }, openEndDatePicker)
      setCostState(prev => { return { ...prev, project_phases: _phases } })
    }
  }

  // change input values
  const inputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>, key: string) => {
    const value = key !== 'description' ? event.target.value.replace(/[^0-9]+/g, '') : event.target.value
    const _value = key !== 'description' ? +value : value
    window.clearTimeout(timer.current)
    const _timer = window.setTimeout(() => {
      updateCost(project.id, { ...costState, [key]: _value }, openEndDatePicker)
    }, 1000)
    timer.current = _timer
    setCostState(prev => { return { ...prev, [key]: _value } })
  }

  const renderTimelinesContent2 = () => {
    return (
      <div style={{ display: 'flex', width: '70%', marginTop: '-.3rem' }}>
        <DatepickerComponent
          className='act-details-datepicker'
          inputColor='transparent-color'
          inputBorder={true}
          dateFormat='dd/MMM/yy'
          disabled={!editRight}
          inputWidth='6.5rem'
          inputBorderSmall={false}
          open={openStartDatePicker}
          onClickOutside={() => onClickOutsideStart}
          onInputClick={() => onInputClickStart()}
          onChange={(date: Date) => changeStartDate(date)}
          selected={costState.start_date ? moment(costState.start_date).toDate() : undefined}
          placeholder='Start Date'
        />
        <DatepickerComponent
          className='act-details-datepicker'
          inputColor='transparent-color'
          inputBorder={true}
          dateFormat='dd/MMM/yy'
          disabled={!editRight}
          inputWidth='6.5rem'
          open={openEndDatePicker}
          onClickOutside={() => onClickOutsideEnd}
          onInputClick={() => onInputClickEnd()}
          onChange={(date: Date) => changeEndDate(date)}
          selected={costState.end_date ? moment(costState.end_date).toDate() : undefined}
          placeholder='End Date'
        />
      </div>
    )
  }

  const renderMemberDropdown = (empLen: boolean) => {
    const options = employees.filter(e => e.id !== '').map(e => {
      return {
        key: e.id,
        text: e.username,
        value: e.email
      }
    })
    return (
      <Dropdown
        id='member-dropdown'
        value={costState.assigned_employees ? costState.assigned_employees.map(e => e.email) : []}
        onChange={(e, data) => changeEmployees(data)}
        icon={<Icon style={{ display: 'none' }} />}
        placeholder='Select Members'
        multiple={true}
        selection={true}
        fluid={true}
        closeOnChange={false}
        options={options}
        style={{ display: project.active && !empLen ? 'none' : '', background: '#F9FBFC', border: 'none', zIndex: 2, height: 'auto', marginTop: '-0.8rem', padding: '.2rem 0 .2rem .2rem', borderRadius: '6px' }}
        disabled={!project.active || project.time_tracking_perm.task_permission === 'all_prj_tasks'}
      />
    )
  }

  const renderMemberContent = () => {
    const _employeesList: IEmployee[] = []
    if (costState.assigned_employees) {
      employees.forEach(e => {
        costState.assigned_employees.forEach(se => {
          if (se.email === e.email && !_employeesList.includes(e)) {
            _employeesList.push(e)
          }
        })
      })
    }
    return (
      <div id='member-component' style={{ padding: '.5rem 0 .5rem 0', marginTop: '-.3rem' }}>
        {_employeesList.length !== 0 ?
          <div id='member-icons' style={{ display: 'flex', marginTop: '-.5rem', marginLeft: '.3rem' }}>
            <TeamGroupComponent size='2rem' space={true} key={`${project.id}-${_employeesList.length}`} team={_employeesList} />
          </div> : null}
        {renderMemberDropdown(_employeesList.length === 0)}
      </div>
    )
  }

  const renderPhasesContent = () => {
    const options = project.phases.map((l, idx) => {
      return {
        key: idx,
        text: (
          <Label
            content={<p style={{ overflowWrap: 'break-word' }}>{l.name}</p>}
            style={{
              background: l.color + '33',
              color: l.color,
              borderRadius: '.57rem',
              height: '1.9rem',
              maxHeight: '6rem',
              minWidth: '4.5rem',
              padding: '.2rem .4rem',
              textAlign: 'center'
            }}
          />
        ),
        value: l.id
      }
    })
    return (
      <Dropdown
        floating={true}
        value={costState.project_phases ? costState.project_phases.map(l => l.id) : []}
        onChange={(e, data) => changePhases(data)}
        className={'act-details-dropdown-labels'}
        icon={<Icon name='chevron down' style={{ display: 'none' }} />}
        placeholder='Allocate Phases'
        multiple={true}
        selection={true}
        fluid={true}
        closeOnChange={false}
        options={options}
        style={{ padding: 0, background: '#F9FBFC' }}
        disabled={!project.active}
      />
    )
  }

  const renderLabelsContent = () => {
    const options = project.labels.map((l, idx) => {
      return {
        key: idx,
        text: (
          <Label
            key={idx}
            style={{
              fontSize: '1rem',
              letterSpacing: '.25px',
              fontWeight: 500,
              background: l.color,
              color: '#172533',
              borderRadius: '5px',
              height: '1.5rem',
              marginLeft: '.3rem',
              padding: '.3rem .4rem 0 .3rem'
            }}
          >
            <Icon name='hashtag' style={{ fontSize: '0.8rem', fontWeight: 500, color: '#172533', marginRight: '0.2rem' }} />{l.name}
          </Label>
        ),
        value: l.id
      }
    })
    return (
      <Dropdown
        floating={true}
        value={costState.labels ? costState.labels.map(l => l.id) : []}
        onChange={(e, data) => changeLabels(data)}
        className={'act-details-dropdown-labels'}
        icon={<Icon name='chevron down' style={{ display: 'none' }} />}
        placeholder='Allocate Labels'
        multiple={true}
        selection={true}
        fluid={true}
        closeOnChange={false}
        options={options}
        style={{ padding: 0, background: '#F9FBFC' }}
        disabled={!project.active}
      />
    )
  }

  const renderDescriptionContent = () => {
    return (
      <div>
        <TextArea
          value={costState.description ? costState.description : ''}
          onChange={e => inputChange(e, 'description')}
          className={!project.active ? 'act-disable-textarea' : 'act-overview-textarea'}
          style={{ resize: 'none', minWidth: '100%', maxHeight: '6rem', border: 'none', marginLeft: '.1rem', borderRadius: '6px', background: '#F9FBFC' }}
          placeholder='Add a description...'
        />
      </div>
    )
  }

  const renderExpendituresContent = () => {
    const currency = currencyFormat(costState.expenditures ? +costState.expenditures : 0.00).replace(/\s/g, '')
    const total_costs = currencyFormat(+costState.unit_price * +costState.quantity).replace(/\s/g, '')
    return (
      <div style={{ display: 'flex', alignItems: 'baseline', marginLeft: '.5rem', marginTop: '-.2rem' }}>
        <div style={{ marginTop: '-.5rem', fontSize: '1.7rem', color: '#08D6A1', letterSpacing: '.25px', lineHeight: 0, display: 'flex', alignItems: 'baseline' }}>
          <p>{currency[0]} {currency.split(',')[0].replace(currency[0], '')}</p>
          <p style={{ color: '#BEC8C3' }}>,{currency.split(',')[1]}</p>
          <p style={{ color: '#172533', fontSize: '1.2rem', paddingLeft: '.3rem' }}>/</p>
        </div>
        <p style={{ color: '#b7b7b7', marginLeft: '.1rem' }}>{total_costs[0]}</p>
        <div style={{ marginLeft: '.1rem' }}>{total_costs.substring(1)}</div>
      </div>
    )
  }

  const renderTotalContent = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'baseline', marginLeft: '.5rem', marginTop: '-.2rem' }}>
        <div style={{ fontSize: '1.7rem', color: '#08D6A1', letterSpacing: '.25px', lineHeight: 0, display: 'flex', alignItems: 'baseline' }}>
          {durationToHours(+cost.current_hours)}h
          <p style={{ color: '#172533', fontSize: '1.2rem', paddingLeft: '.3rem' }}>/</p>
        </div>
        <div style={{ display: 'flex' }}>{costState.total_hours}<p style={{ color: '#b7b7b7', marginLeft: '.1rem' }}>h</p></div>
      </div>
    )
  }

  const renderColumns = () => {
    const columns = [
      { image: timelines, text: 'Timelines', content: (renderTimelinesContent2()) },
      { image: phase, margin: '.1rem', text: 'Phase', content: (renderPhasesContent()) },
      { image: total_hours, text: 'Total Hours', content: (renderTotalContent()) },
      { image: budget, margin: '.2rem', text: 'Expenditures', content: (renderExpendituresContent()) },
      { image: member, text: 'Member', content: (renderMemberContent()) },
      { image: labels, margin: '.1rem', text: 'Labels', content: (renderLabelsContent()) },
      { image: description, margin: '.1rem', text: 'Description', content: (renderDescriptionContent()) }
    ]
    if (project.permissions === 'open') {
      columns.splice(4, 1)
    }
    return columns.map((col, idx) => {
      return (
        <Grid.Row key={idx} style={{ height: col.text === 'Labels' ? 'auto' : '3rem', paddingBottom: col.text === 'Labels' ? 0 : '' }}>
          <Grid.Column width={1} textAlign='left' style={{ padding: 0 }}><Image src={col.image} style={{ minWidth: '1rem', marginTop: col.margin ? col.margin : '' }} /></Grid.Column>
          <Grid.Column width={4} textAlign='left' style={{ padding: 0, color: '#172533', letterSpacing: '.25px', fontSize: '1.2rem', fontWeight: 'bold' }}>{col.text}</Grid.Column>
          <Grid.Column width={9} textAlign='left' style={{ padding: 0 }}>{col.content}</Grid.Column>
        </Grid.Row>
      )
    })
  }

  return (
    <React.Fragment>
      <Grid verticalAlign='top' columns={3} style={{ margin: '3% 0 0 6%' }}>
        {renderColumns()}
      </Grid>
    </React.Fragment>
  )
}

export default PassTroughDetailsOverviewComponent
